/**
 * aC Men's Hub の課題管理の、課題の判定と次にやること（判定表 BR-19 の行と待ちの様子）の語彙（zod schema + 型）。
 *
 * 判定は台帳の語彙（`issue-mgmt.ts`）が課題の形に、次にやることは一覧・詳細・やることのカードが使う。
 * 判定表の当て方そのものは API（`apps/api/ac-mens-hub` の `domain/next-action.ts`）が持ち、ここは返す形だけを決める。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の判定と、課題ごとの次にやること（判定表の行・受け持つ人・期限・案件を確かめる理由）と待ちの様子の形を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 課題の判定。統合済みは統合の操作でだけ付く。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題の判定（未判定・対応する・保留・対応しない・統合済み）
 * @graph-connects none
 */
export const IssueVerdictSchema = z.enum(['pending', 'accepted', 'on_hold', 'rejected', 'merged']);
/**
 * 案件を担当に確かめてもらう理由。not_found＝続けて見つからない、deleted＝Backlog で削除された、
 * on_hold＝保留中、unknown_status＝対応表に無い状態名、missing_status＝ミラーの行に状態名が無い、
 * overdue＝期限日を過ぎて未完了。
 * @graph-connects none
 */
export const CaseAttentionSchema = z.enum([
    'not_found',
    'deleted',
    'on_hold',
    'unknown_status',
    'missing_status',
    'overdue',
]);
/**
 * 次にやること（判定表 BR-19）を受け持つ役。judge＝課題管理の設定の判定者、assignee＝課題の担当（空なら判定者）。
 * @graph-connects none
 */
export const NextActionRoleSchema = z.enum(['judge', 'assignee']);
/**
 * 次にやることの急ぎ具合（判定表の重大度）。画面には出さず、やることと一覧の並び順にだけ使う。
 * @graph-connects none
 */
export const NextActionSeveritySchema = z.enum(['high', 'medium', 'low']);
/**
 * 「対応する」の必須項目で欠けているもの（担当がキーを失ったときも assignee）。
 * @graph-connects none
 */
export const NextActionMissingFieldSchema = z.enum(['assignee', 'priority', 'devRequired']);
/**
 * 判定し直す理由。hold_date＝保留の見直す日が来た、new_voices＝判定（解決の確認）の後に投稿された声が紐づいた、
 * changed＝判定者以外の人が判定した。
 * @graph-connects none
 */
export const RedecideReasonSchema = z.enum(['hold_date', 'new_voices', 'changed']);
/**
 * 次にやることに共通する項目。
 * - row: 判定表の何行目か（1〜11）
 * - who: 受け持つ人（判定者・担当が決まっていなければ null）
 * - dueDate: 期限（日本時間の YYYY-MM-DD。その日の 23:59 まで）。期限の無い行は null
 * - since: 期限の無い行で、出た日（分かるときだけ）
 * - otherCount: 同じ課題でほかに当たっている行の数（画面の「ほかN件」）
 * @graph-connects none
 */
const NextActionBaseSchema = z.object({
    row: z.number().int().min(1).max(11),
    severity: NextActionSeveritySchema,
    role: NextActionRoleSchema,
    who: z.object({ email: z.string(), displayName: z.string() }).nullable(),
    dueDate: z.string().nullable(),
    overdue: z.boolean(),
    since: z.string().nullable(),
    otherCount: z.number().int().min(0),
});
/**
 * 課題ごとの次にやること（判定表 BR-19 の一番上に当たった行）。kind は、やることのカードの種類と詳細の大ボタンを決める。
 * - decide（行1・6）: 判定する
 * - fill（行2）: 担当・優先度・開発要否を決める（missing は欠けている項目）
 * - redecide（行5）: 判定し直す（reason・新しい声の数・判定した人・前の判定）
 * - check_case（行3・8）: 案件を Backlog で確かめる（attention が overdue なら行8）
 * - link_case（行4・7）: 起案して案件を紐づける（unresolvedAt は未解決の印の日）
 * - verify_fix（行9）: 直ったか確かめる
 * - reply（行10）: 声を上げた人に結果を返す（pendingCount はまだ返していない声の数）
 * - start_check（行11）: 開発しない対応に着手したか確かめる
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題ごとに次にやること（判定表の行・受け持つ人・期限・期限切れ・ほかに当たった行の数と、カードの種類ごとの中身）を表す
 * @graph-connects none
 */
export const IssueNextActionSchema = z.discriminatedUnion('kind', [
    NextActionBaseSchema.extend({ kind: z.literal('decide') }),
    NextActionBaseSchema.extend({
        kind: z.literal('fill'),
        missing: z.array(NextActionMissingFieldSchema),
    }),
    NextActionBaseSchema.extend({
        kind: z.literal('redecide'),
        reason: RedecideReasonSchema,
        newVoiceCount: z.number().int().min(0),
        changedBy: z.string().nullable(),
        previousVerdict: IssueVerdictSchema.nullable(),
    }),
    NextActionBaseSchema.extend({
        kind: z.literal('check_case'),
        caseKey: z.string(),
        attention: CaseAttentionSchema,
    }),
    NextActionBaseSchema.extend({
        kind: z.literal('link_case'),
        unresolvedAt: z.string().nullable(),
    }),
    NextActionBaseSchema.extend({ kind: z.literal('verify_fix') }),
    NextActionBaseSchema.extend({
        kind: z.literal('reply'),
        pendingCount: z.number().int().min(1),
    }),
    NextActionBaseSchema.extend({ kind: z.literal('start_check') }),
]);
/**
 * 次にやることが無い課題の待ちの様子（判定表の行12・13）。
 * - cases: Backlog の進み待ち（紐づく案件の数と完了の数）
 * - working: 担当が対応中（開発しない対応）
 * - review_on: 保留で、見直す日を待っている
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 次にやることが無い課題の待ちの様子（Backlog の進み待ち・担当が対応中・見直す日待ち）を表す
 * @graph-connects none
 */
export const IssueWaitingSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('cases'),
        total: z.number().int(),
        completed: z.number().int(),
    }),
    z.object({ kind: z.literal('working'), assigneeName: z.string().nullable() }),
    z.object({ kind: z.literal('review_on'), date: z.string() }),
]);
//# sourceMappingURL=issue-mgmt-status.js.map